/** @format */
/* eslint-disable */

import Display from './display';
import Day from './day';
import { $csI18n } from '@/js/modules/csI18n';

export const $_rangeMode = {
    /**
     * @public
     * @description
     * Updates the calendar dates depending on whether we are targeting the start or end
     * range date. We will then assign any dates added to our instance.displayDateStamp
     * 
     * @param {Object} dateSelected the currently selected date
     * @param {Object} instance - the current active datepicker instance
     */
	updateDisplayDateStamp(dateSelected, instance) {
		const selectedDates = Array.isArray(instance.selectedDates) ? instance.selectedDates : [];
		const datesToAdd = [];
		if (selectedDates.length > 1) {
			//we have both dates already set. Find and move the relevant active date.
			const activeSelectedItemIndex = instance.activeSelectedDate.selectedDateItemRef;
			instance.updateActiveSelectedDate(dateSelected, activeSelectedItemIndex);

			if (activeSelectedItemIndex == 1) {
                
				//we are targeting the end of the range
				if (Day.isBefore(dateSelected.date.jsDateRef, selectedDates[0].date.jsDateRef)) {
					//our end date is before the current start date. Lets clean up and switch
					datesToAdd.push(dateSelected.date.jsDateRef);
					instance.updateActiveSelectedDate(dateSelected, 0);
				} else {
					datesToAdd.push(selectedDates[0].date.jsDateRef);
					datesToAdd.push(dateSelected.date.jsDateRef);
				}
			} else {
				//we are targeting the start of the range
				if (Day.isAfter(dateSelected.date.jsDateRef, selectedDates[1].date.jsDateRef)) {
					//our start date is before the current end date. Lets clean up and switch
					datesToAdd.push(dateSelected.date.jsDateRef);
					instance.updateActiveSelectedDate(dateSelected, 0);
				} else {
					datesToAdd.push(dateSelected.date.jsDateRef);
					datesToAdd.push(selectedDates[1].date.jsDateRef);
				}
			}
		} else if (selectedDates.length == 1) {
			//we are targeting the end of the range 
			if (Day.isBefore(dateSelected.date.jsDateRef, selectedDates[0].date.jsDateRef)) {
				//our end date is before the current start date. Lets clean up and switch
				datesToAdd.push(dateSelected.date.jsDateRef);
				datesToAdd.push(instance.selectedDates[0].date.jsDateRef);
				instance.updateActiveSelectedDate(dateSelected, 0);
			} else {
				//we have one - add the new date
				datesToAdd.push(instance.selectedDates[0].date.jsDateRef);
				datesToAdd.push(dateSelected.date.jsDateRef);
				instance.updateActiveSelectedDate(dateSelected, 1);
			}
		} else {
			// we have none - create the first
			datesToAdd.push(dateSelected.date.jsDateRef);
			instance.updateActiveSelectedDate(dateSelected, 0);
		}

		instance.displayDateStamp = {
			start: datesToAdd[0] || undefined,
			end: datesToAdd[1] || undefined
		};
	},

    /**
     * @public
     * @description
     * Takes the current displayDateStamp and assigns them to a start and end property for the range
     * if applicable.
     * 
     * @param {Object} instance the active datepicker instance
     * 
     * @returns {Array} of 2 dates, start and end (filtered by whether or not they are selectable)
     */
	buildSelectedDatesFromDatestamp(instance) {
		const rangeDates = Object.assign(
			{
				start: undefined,
				end: undefined
			},
			instance.displayDateStamp
		);

        const datesToAdd = [];
        
        //assign the start date if we have one
		if (rangeDates.start != undefined) {
			const startDate = Day.getDateObjectFromDateString(rangeDates.start, instance);
			if (startDate.date.isSelectable) {
				datesToAdd.push(startDate);
			} else {
				throw new Error($csI18n.t('datepicker.unassignableDate', [startDate.dateString]));
			}
		} else {
            //show todays date in the calendar if we don't have a start date
			Display.updateCalendarViewFromDate(instance.today, instance);
		}

        //if we have a date to assign to 'end' do it here.
		if (rangeDates.end != undefined && datesToAdd.length > 0) {
			const endDate = Day.getDateObjectFromDateString(rangeDates.end, instance);
			if (!endDate.date.isSelectable) {
				throw new Error($csI18n.t('datepicker.unassignableDate', [endDate.dateString]));
            }
            
			datesToAdd.push(Object.assign(endDate, {
                selectedDateItemRef: 1 //update the reference of where this date lies within our selectedDates
            }));
		}

        //make sure the display reflects the latest selected date
		if (instance.activeSelectedDate) {
			Display.updateCalendarViewFromDate(instance.activeSelectedDate, instance);
		}

		return [...datesToAdd.filter(({ date }) => date.isSelectable === true)];
	},

    /**
     * @public
     * @description
     * Check to see if the date passed in is valid for the range available
     * 
     * @param {Object} monthDate - the current selected date
     * @param {String} calendarMode - this will always be 'range' for this
     * @param {Array} selectedDates - the currently selected dates in the calendar
     * @param {Object} instance - the active datepicker instance.
     * 
     * @returns {Boolean} 
     */
	isDateInAvailableRange(monthDate, calendarMode, selectedDates, instance) {
		if (calendarMode !== 'range' || !monthDate.date.isSelectable || selectedDates.length == 1) return false;
		const firstSelected = selectedDates[0] || undefined;
		const secondSelected = selectedDates[1] || undefined;
		let timestampOne = firstSelected != undefined ? firstSelected.date.jsDateRef : undefined;
		let timestampTwo = secondSelected != undefined ? secondSelected.date.jsDateRef : undefined;
		if (instance.hoveredDate != undefined && timestampOne != undefined && timestampTwo == undefined) {
			if (Day.isAfter(monthDate.date.jsDateRef, instance.hoveredDate.date.jsDateRef)) {
				return Day.isAfter(monthDate.date.jsDateRef, instance.hoveredDate.date.jsDateRef) && Day.isBefore(monthDate.date.jsDateRef, timestampOne);
			}
			return Day.isBefore(monthDate.date.jsDateRef, instance.hoveredDate.date.jsDateRef) && Day.isAfter(monthDate.date.jsDateRef, timestampOne);
		}
		return Day.isBefore(monthDate.date.jsDateRef, timestampTwo) && Day.isAfter(monthDate.date.jsDateRef, timestampOne);
	},

    /**
     * @public
     * @description
     * Checks that none of the selected dates match todays date (used to hide the 'NOW' button)
     * 
     * @param {Object} instance the current datepicker instance
     */
	nowIsSelectable(instance) {
		const today = instance.today;
		return !instance.selectedDates.some(({ date }) => date.jsDateRef == today.date.jsDateRef);
	}
};

export {$_rangeMode as default};
